import { useState, useRef } from 'react';

/**
 * Custom hook to manage all game state for the Qwixx game
 * Consolidates 30+ useState calls from GamePage.jsx into a single hook
 *
 * @param {string} gameId - The unique game identifier
 * @param {string} playerName - The current player's name
 * @returns {object} - Game state and setter functions
 */
export const useGameState = (gameId, playerName) => {
  // Dice state
  const [dice, setDice] = useState({
    white1: null,
    white2: null,
    red: null,
    yellow: null,
    green: null,
    blue: null
  });

  const [selectedWhiteDie, setSelectedWhiteDie] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);

  // Scorecard state
  const [scorecard, setScorecard] = useState({
    red: [],
    yellow: [],
    green: [],
    blue: [],
    penalties: 0
  });

  // Row management
  const [closedRows, setClosedRows] = useState({
    red: false,
    yellow: false,
    green: false,
    blue: false
  });

  const [rowClosers, setRowClosers] = useState({
    red: [],
    yellow: [],
    green: [],
    blue: []
  });

  const [activeDice, setActiveDice] = useState({
    red: true,
    yellow: true,
    green: true,
    blue: true
  });

  const [pendingRowClosures, setPendingRowClosures] = useState({
    red: false,
    yellow: false,
    green: false,
    blue: false
  });

  const [rowClosureOverlays, setRowClosureOverlays] = useState({
    red: false,
    yellow: false,
    green: false,
    blue: false
  });

  // Player and turn state
  const [currentPlayer, setCurrentPlayer] = useState(playerName);
  const [players, setPlayers] = useState([
    { name: playerName, isHost: false }
  ]);

  // Game flow state
  const [gameStatus, setGameStatus] = useState('waiting'); // 'waiting', 'rolling', 'marking', 'finished'
  const [turnPhase, setTurnPhase] = useState('initial'); // 'initial', 'whiteSum', 'colorDice', 'complete'
  const [isLoading, setIsLoading] = useState(true);
  const [autoEndingTurn, setAutoEndingTurn] = useState(false);

  // Turn actions tracking
  const [turnActions, setTurnActions] = useState({
    whiteSumMarked: false,
    colorDiceMarked: false,
    whiteSumSkipped: false
  });

  // UI state
  const [message, setMessage] = useState('');
  const [connectionError, setConnectionError] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);
  const [guidanceStep, setGuidanceStep] = useState('');

  // White phase tracking
  const [playerWhitePhaseComplete, setPlayerWhitePhaseComplete] = useState({});

  // Game initialization
  const [gameInitialized, setGameInitialized] = useState(false);
  const [showFirstPlayerModal, setShowFirstPlayerModal] = useState(false);
  const [spinningWheel, setSpinningWheel] = useState(false);
  const [selectedFirstPlayer, setSelectedFirstPlayer] = useState(null);

  // Game end state
  const [allPlayerScores, setAllPlayerScores] = useState({});
  const [gameWinner, setGameWinner] = useState(null);
  const [allPlayerScorecards, setAllPlayerScorecards] = useState({});
  const [allPlayerClosedRows, setAllPlayerClosedRows] = useState({});
  const [allPlayerRowClosers, setAllPlayerRowClosers] = useState({});

  // Scorecard zoom
  const [scorecardZoom, setScorecardZoom] = useState(1);

  // Modal state
  const [showRulesModal, setShowRulesModal] = useState(false);

  // Chat state
  const [showChat, setShowChat] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Facebook share state
  const [shareStatus, setShareStatus] = useState('');

  // Turn timer state
  const [turnTimerEnabled, setTurnTimerEnabled] = useState(false);
  const [turnTimerDuration, setTurnTimerDuration] = useState(30);
  const [timerEndTime, setTimerEndTime] = useState(null);

  // Opponent scores visibility state
  const [showOpponentScores, setShowOpponentScores] = useState(false);
  const [allPlayerLiveScores, setAllPlayerLiveScores] = useState({});

  // Refs
  const scorecardRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const overlayTimeoutsRef = useRef({
    red: null,
    yellow: null,
    green: null,
    blue: null
  });

  // Return all state and setters in a structured object
  return {
    // Dice
    dice,
    setDice,
    selectedWhiteDie,
    setSelectedWhiteDie,
    selectedColor,
    setSelectedColor,

    // Scorecard
    scorecard,
    setScorecard,

    // Rows
    closedRows,
    setClosedRows,
    rowClosers,
    setRowClosers,
    activeDice,
    setActiveDice,
    pendingRowClosures,
    setPendingRowClosures,
    rowClosureOverlays,
    setRowClosureOverlays,

    // Players and turns
    currentPlayer,
    setCurrentPlayer,
    players,
    setPlayers,

    // Game flow
    gameStatus,
    setGameStatus,
    turnPhase,
    setTurnPhase,
    isLoading,
    setIsLoading,
    autoEndingTurn,
    setAutoEndingTurn,

    // Turn actions
    turnActions,
    setTurnActions,

    // UI
    message,
    setMessage,
    connectionError,
    setConnectionError,
    showGuidance,
    setShowGuidance,
    guidanceStep,
    setGuidanceStep,

    // White phase
    playerWhitePhaseComplete,
    setPlayerWhitePhaseComplete,

    // Initialization
    gameInitialized,
    setGameInitialized,
    showFirstPlayerModal,
    setShowFirstPlayerModal,
    spinningWheel,
    setSpinningWheel,
    selectedFirstPlayer,
    setSelectedFirstPlayer,

    // Game end
    allPlayerScores,
    setAllPlayerScores,
    gameWinner,
    setGameWinner,
    allPlayerScorecards,
    setAllPlayerScorecards,
    allPlayerClosedRows,
    setAllPlayerClosedRows,
    allPlayerRowClosers,
    setAllPlayerRowClosers,

    // Scorecard zoom
    scorecardZoom,
    setScorecardZoom,

    // Modals
    showRulesModal,
    setShowRulesModal,

    // Chat
    showChat,
    setShowChat,
    unreadMessages,
    setUnreadMessages,

    // Facebook share
    shareStatus,
    setShareStatus,

    // Turn timer
    turnTimerEnabled,
    setTurnTimerEnabled,
    turnTimerDuration,
    setTurnTimerDuration,
    timerEndTime,
    setTimerEndTime,

    // Opponent scores visibility
    showOpponentScores,
    setShowOpponentScores,
    allPlayerLiveScores,
    setAllPlayerLiveScores,

    // Refs
    scorecardRef,
    loadingTimeoutRef,
    overlayTimeoutsRef,

    // Computed values
    isGameOver: gameStatus === 'finished',
    isActivePlayer: currentPlayer === playerName,
    whiteDiceSum: dice.white1 && dice.white2 ? dice.white1 + dice.white2 : null
  };
};

export default useGameState;
